/** Angular Imports */
import { inject } from '@angular/core';
import { CanMatchFn, Router } from '@angular/router';

/** Core Imports */
import { AuthService } from '@core/services/auth/auth.service';

/** Authenticated traits label for global administrators (matches AuthService user traits). */
export const SUPER_ADMIN_ROLE_NAME = 'Super Admin';

/**
 * Route guard that prevents Dealers and Dealer Admins from accessing restricted routes.
 *
 * @param route - The activated route snapshot
 * @param segments - The URL segments
 * @returns {boolean | import('@angular/router').UrlTree} True if allowed, otherwise redirects to /dashboard
 *
 * @remarks
 * Specifically restricts 'Dealer' and 'Dealer Admin' roles from accessing the /users management area.
 * Redirects unauthorized users to the dashboard.
 */
export const roleGuard: CanMatchFn = (route, segments) => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const userTraits = authService.getUserTraits();

    // If no traits found, we let it pass (authGuard handles auth)
    if (!userTraits) {
        return true;
    }

    const roleName = userTraits.data?.roleName;

    // Restrict Dealers and Dealer Admins from accessing the route
    if (roleName === 'Dealer' || roleName === 'Dealer Admin') {
        return router.createUrlTree(['/dashboard']);
    }

    return true;
};

/**
 * Allows matching routes only when the signed-in user has the Super Admin role.
 * Redirects everyone else to `/users` so deep links to user details cannot bypass the list UX.
 */
export const superAdminOnlyGuard: CanMatchFn = () => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const roleName = authService.getUserTraits()?.data?.roleName;

    if (roleName !== SUPER_ADMIN_ROLE_NAME) {
        return router.createUrlTree(['/users']);
    }

    return true;
};
